import {
  Component,
  computed,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import { Router } from '@angular/router';

import { take } from 'rxjs';

import { DirectoryUrlService } from '@sondermind/data-access/client-facing/directory-navigation';
import { InsuranceActionType } from '@sondermind/data-access/client-insurance';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { IconSizes } from '@sondermind/iris/icon';
import { CurrentUser } from '@sondermind/data-access/client-user';

export enum CoverageNotificationType {
  EXPIRED = 'expired',
  NO_ACTIVE_COVERAGE = 'no_active_coverage',
  COVERAGE_UNVERIFIED = 'coverage_unverified',
  NO_COVERAGE = 'no_coverage',
  EAP_SESSIONS_LOW = 'eap_sessions_low',
  EAP_SESSIONS_OUT_OF = 'eap_sessions_out_of',
  EAP_EXPIRING_SOON = 'eap_expiring_soon',
  EAP_EXPIRED = 'eap_expired',
  OUT_OF_NETWORK = 'out_of_network',
  ADD_PAYMENT_METHOD = 'add_payment_method'
}

export interface ICoverageNotificationConfig {
  title: string;
  subtitle: string;
  actionText: string;
  iconName: string;
  actionLinkClick?: () => void;
  linkClick?: () => void;
  link?: string;
}

@Component({
  selector: 'client-coverage-notification-banner',
  templateUrl: './coverage-notification-banner.component.html',
  styleUrls: ['./coverage-notification-banner.component.scss'],
  standalone: false
})
export class CoverageNotificationBannerComponent implements OnInit {
  readonly notificationType = input<CoverageNotificationType>();
  readonly onInsurancePage = input<boolean>(false);
  readonly coverageStartDate = input<string>();
  readonly numberofPastSessions = input<number>(0);
  updateBenefits = output<InsuranceActionType | null>();

  readonly isDismissed = signal(false);

  irisButtonTypes = IrisButtonTypes;
  iconSizes = IconSizes;
  irisButtonContainerAlignments = IrisButtonContainerAlignments;

  readonly config = computed(() => this.getConfigForType(this.notificationType()));
  readonly iconName = computed(() => this.config()?.iconName || '');
  readonly isCashPay = computed(() => this.notificationType() === CoverageNotificationType.NO_COVERAGE);
  readonly isOutOfNetwork = computed(() => this.notificationType() === CoverageNotificationType.OUT_OF_NETWORK);
  readonly isEapWarning = computed(
    () => this.notificationType() === CoverageNotificationType.EAP_SESSIONS_LOW ||
    this.notificationType() === CoverageNotificationType.EAP_EXPIRING_SOON
  );
  readonly isEapAlert = computed(
    () => this.notificationType() === CoverageNotificationType.EAP_SESSIONS_OUT_OF ||
    this.notificationType() === CoverageNotificationType.EAP_EXPIRED
  );
  readonly isAddPaymentMethod = computed(
    () => this.notificationType() === CoverageNotificationType.ADD_PAYMENT_METHOD
  );
  readonly backgroundClass = computed(() => {
    if (this.isCashPay()) {
      return 'no-coverage';
    }

    if (this.isOutOfNetwork()) {
      return this.onInsurancePage() ? 'cash-pay-without-button' : 'cash-pay-with-button';
    }

    if (this.isEapWarning()) {
      return 'eap-warning';
    }

    return 'default-background';
  });

  readonly showDismissButton = computed(() => this.isCashPay() && !this.onInsurancePage());

  selfPayNotificationDismissed = 'selfPayNotificationDismissed';
  outOfNetworkNotificationDismissed = 'outOfNetworkNotificationDismissed';

  constructor(
    private router: Router,
    private directoryUrlService: DirectoryUrlService,
    private currentUser: CurrentUser
  ) {}

  ngOnInit(): void {
    // Hide self pay notification if user has dismissed it or if user has had more than 3 past sessions
    if (this.showDismissButton() &&
    (localStorage.getItem(this.selfPayNotificationDismissed) === 'true' || this.numberofPastSessions() > 3)) {
      this.isDismissed.set(true);
    }
    if (this.isOutOfNetwork() && localStorage.getItem(this.outOfNetworkNotificationDismissed) === 'true') {
      this.isDismissed.set(true);
    }
  }

  onUpdateBenefits(): void {
    this.updateBenefits.emit(null);
  }

  onAddPaymentMethod(): void {
    this.updateBenefits.emit(InsuranceActionType.CONTINUE_WITH_SELF_PAY);
  }

  onAddInsurance(): void {
    this.updateBenefits.emit(InsuranceActionType.ADD_INSURANCE);
  }

  onDismiss(): void {
    this.isDismissed.set(true);
    localStorage.setItem(this.selfPayNotificationDismissed, 'true');
  }

  dismissOutOfNetworkNotification(): void {
    this.isDismissed.set(true);
    localStorage.setItem(this.outOfNetworkNotificationDismissed, 'true');
  }

  private getConfigForType(type: CoverageNotificationType): ICoverageNotificationConfig {
    switch (type) {
      case CoverageNotificationType.EXPIRED:
        return {
          title: 'Insurance coverage has ended',
          subtitle: 'Add new insurance before your next session to avoid paying the self-pay rate.',
          actionText: 'Update insurance',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
      case CoverageNotificationType.COVERAGE_UNVERIFIED:
        return {
          title: "We're verifying your insurance coverage",
          subtitle: "We'll contact you if more info is needed and will always discuss options with you before any billing changes occur.",
          actionText: 'Update insurance',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
      case CoverageNotificationType.NO_ACTIVE_COVERAGE: {
        const startDate = this.coverageStartDate();
        const formattedDate = startDate ? this.formatDate(startDate) : 'the start date';

        return {
          title: 'Insurance coverage not started',
          subtitle: `Your insurance plan starts on ${formattedDate}. Add new insurance so your sessions are covered.`,
          actionText: 'Update insurance',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
      }
      case CoverageNotificationType.NO_COVERAGE:
        return {
          title: 'Not using insurance',
          subtitle: this.onInsurancePage() ?
            "You'll pay the self-pay rate for sessions unless you add insurance." :
            'If you have insurance or other benefits, you can add them to help cover the cost of your sessions',
          actionText: 'Add insurance',
          iconName: '',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
      case CoverageNotificationType.EAP_SESSIONS_LOW:
        return {
          title: 'EAP sessions are almost out',
          subtitle: this.onInsurancePage() ?
            "Add your health insurance or a payment method so you're all set to continue your sessions. You can continue with self-pay by adding a payment method." :
            "You'll need to add other coverage such as your regular health insurance or new EAP benefits.",
          actionText: 'Add insurance',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
      case CoverageNotificationType.EAP_SESSIONS_OUT_OF:
        return {
          title: 'Out of EAP sessions',
          subtitle: 'Add new insurance benefits to avoid paying the self-pay rate for your next session. You can continue with self-pay by adding a payment method.',
          actionText: 'Add insurance',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
      case CoverageNotificationType.EAP_EXPIRING_SOON:
        return {
          title: 'EAP benefits expiring soon',
          subtitle: "Add your health insurance or a payment method so you're all set to continue your sessions. You can continue with self-pay by adding a payment method.",
          actionText: 'Add insurance',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
      case CoverageNotificationType.EAP_EXPIRED:
        return {
          title: 'EAP benefits expired',
          subtitle: 'Add new insurance benefits to avoid paying the self-pay rate for your next session. You can continue with self-pay by adding a payment method.',
          actionText: 'Add insurance',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
      case CoverageNotificationType.OUT_OF_NETWORK:
        return {
          title: 'Some of your providers may not accept your verified insurance.',
          subtitle: "Check for a provider's in-network status before scheduling to avoid self-pay. You can always continue with providers you've connected with or ",
          actionText: 'Got it',
          iconName: '',
          actionLinkClick: () => {
            this.dismissOutOfNetworkNotification();
          },
          linkClick: () => {
            this.directoryUrlService.computeDirectoryUrl().pipe(take(1)).subscribe((url) => {
              this.router.navigate([url]);
            });
          },
          // making link text empty will prevent display for a VA user
          link: !this.currentUser.isVeteransAffairs() ? 'explore in-network provider options.' : ''
        };
      case CoverageNotificationType.ADD_PAYMENT_METHOD:
        return {
          title: 'Missing payment method',
          subtitle: 'Please add your payment information for any copays or costs not covered by your insurance.',
          actionText: 'Add payment method',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onAddPaymentMethod();
          }
        };
      default:
        return {
          title: 'Insurance issue detected',
          subtitle: 'Please review your insurance information.',
          actionText: 'Update benefits',
          iconName: 'warning',
          actionLinkClick: () => {
            this.onUpdateBenefits();
          }
        };
    }
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });
  }
}
